/**
 * The billing coefficient of one mission (JBWEB-1201).
 *
 * The api is authoritative and applies the same rules: these only spare the
 * admin a round trip. An empty value means "no override": the mission follows
 * the company grid.
 */

export const MISSION_COEFFICIENT_ERROR = 'INVALID_MISSION_COEFFICIENT';

const COEFFICIENT_PRECISION = 4;
const COEFFICIENT_MAX = 10;

// A trailing separator is accepted so that "1," reads as valid while typing.
const COEFFICIENT_PATTERN = /^(\d+[.,]?\d*|[.,]\d+)$/;

const roundCoefficient = (value) =>
  Math.round(value * 10 ** COEFFICIENT_PRECISION) / 10 ** COEFFICIENT_PRECISION;

const invalid = () => ({ value: null, error: MISSION_COEFFICIENT_ERROR });

/**
 * @param input a number, or the text of the field (comma or dot)
 * @returns `{ value, error }`: `value` is the override to store (null = the
 *          company value), `error` is null or MISSION_COEFFICIENT_ERROR
 */
export const parseMissionCoefficient = (input) => {
  if (input === null || input === undefined)
    return { value: null, error: null };

  let parsed;
  if (typeof input === 'number') {
    parsed = input;
  } else if (typeof input === 'string') {
    const text = input.trim();
    if (text === '') return { value: null, error: null };
    if (!COEFFICIENT_PATTERN.test(text)) return invalid();
    parsed = Number(text.replace(',', '.'));
  } else {
    return invalid();
  }

  if (!Number.isFinite(parsed)) return invalid();
  // Checked on the stored value: 9.99999 would otherwise be kept as 10.
  const rounded = roundCoefficient(parsed);
  if (!(rounded > 0) || !(rounded < COEFFICIENT_MAX)) return invalid();
  return { value: rounded, error: null };
};

/** 1.8 -> '1,80', 2.0525 -> '2,0525'; null when there is no value. */
export const formatMissionCoefficient = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number =
    typeof value === 'string' ? Number(value.replace(',', '.')) : value;
  if (typeof number !== 'number' || !Number.isFinite(number)) return null;
  const [integer, decimals] = number.toFixed(COEFFICIENT_PRECISION).split('.');
  return `${integer},${decimals.replace(/0+$/, '').padEnd(2, '0')}`;
};
